/**
 * TRX JS Library payment input — per-revised-directive-#6 (see
 * docs/TRX_MIGRATION.md §0). Loads the TRX `paypage-fields` library,
 * mints a server-side PayPage session via
 * `POST /api/v1/payments/trx/paypage/init`, then renders the per-field
 * iframes the library mounts into containers we own.
 *
 * Visual design — "the slab":
 *   The per-field iframes (card-number / expiry / cvc, or routing /
 *   account) mount into seamless cells inside ONE elevated surface with
 *   hairline dividers — the Stripe-Link / Apple-Pay-sheet pattern, not
 *   three separate bordered boxes. The iframe interiors are styled
 *   transparent/borderless via `stylesCSS` (the only knob TRX gives us
 *   inside the cross-origin frame); everything else — the surface, the
 *   `:focus-within` brand-accent ring, the live card-network mark, the
 *   shimmer load and the error shake — is host DOM under our full
 *   control. The accent tracks the restaurant's `--brand-primary`
 *   (published on :root by CheckoutPage), so the form feels native to
 *   each restaurant.
 *
 * Why `:focus-within` works across the iframe: focusing an input inside
 * a cross-origin iframe focuses the `<iframe>` element itself, so the
 * ancestor slab matches `:focus-within` even though we can't see which
 * field is active. That's the whole-card ring, which is the look we want.
 *
 * Submission contract (imperative ref):
 *   * Parent obtains a ref to this component
 *   * Parent calls `ref.current.extractEncryptedData()` on submit
 *   * Returns the `{ data, keyId, ... }` envelope from the library —
 *     parent forwards it to the backend as `Account.Encrypted{Data,KeyId}`
 *
 * Encrypted-data TTL is 15 minutes per the JS library contract
 * (§11 timing table). Parent is responsible for honoring that — do
 * not extract earlier than the actual submit moment.
 */

import * as Sentry from '@sentry/react';
// The logger by its own subpath and the rest as TYPES only: a value import
// from the client barrel pulls the Firebase SDK into the POS keyed-entry bundle.
import { createClientLogger } from '@order-up/client/logger';
import type {
    TrxPayPageSessionRequest,
    TrxPayPageSessionResponse,
} from '@order-up/client/vite';
import { isPositiveIntegerId, MISSING_LOCATION_CONTEXT } from '../lib/locationScope';
import {
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import {
    TrxCardBrandMark,
    normalizeCardBrand,
    TRX_FIELD_CSS_BASE as FIELD_CSS_BASE,
    TRX_FIELD_CSS_NUMBER as FIELD_CSS_NUMBER,
    isApplePayAvailable,
    partitionTrxInitErrors,
    resolvePayPageRails,
    type CardBrand,
} from '@order-up/common';
import { useTrxPayPageScript } from '../hooks/useTrxPayPageScript';
import type {
    TrxPayPageAchEncryptedResult,
    TrxPayPageCardEncryptedResult,
    TrxPayPageError,
    TrxPayPageFieldsMap,
    TrxPayPageInstance,
    TrxPayPageWalletResult,
} from '../types/trxPayPage';
import { trxErrorMessage, trxErrorType } from '../types/trxPayPage';
import { BANK_FIELDS_REJECTED, CARD_FIELDS_REJECTED } from '@order-up/common';
import type { WalletContact } from '../lib/walletContact';

const logger = createClientLogger('TrxJsLibraryInput');

// ─────────────────────────── public API ────────────────────────────────

/**
 * Encrypted-data payload surfaced to the parent via the imperative
 * ref. Shape is intentionally the smallest superset of card + ACH —
 * `paymentType` discriminates which optional fields are populated.
 */
export interface TrxEncryptedPayload {
    readonly paymentType: 'card' | 'ach';
    readonly data: string;
    readonly keyId: string;
    /** Card only — unencrypted BIN when `includeBIN: true` was set. */
    readonly bin?: string;
    /** ACH only — when `includeACHInfo: true` was set. */
    readonly routingNumber?: string;
    /** ACH only — when `includeACHInfo: true` was set. */
    readonly accountNumberLastFour?: string;
}

/**
 * Apple Pay / Google Pay result surfaced to the parent. The wallet sheet
 * produces an encrypted token blob + a cardSecurity cryptogram (Visa CAVV /
 * Mastercard DSRP). The cryptogram has a ~1-minute TTL — the parent must submit
 * the checkout immediately on receipt.
 */
export interface TrxWalletPaymentResult {
    readonly walletName: 'ApplePay' | 'GooglePay';
    readonly data: string;
    /** The TRX JS-library key id for `data`. */
    readonly keyId?: string;
    readonly bin?: string;
    readonly cardSecurity?: { cavv?: string; dsrpCryptogram?: string };
    /**
     * Payer name / email / billing address collected by the wallet sheet.
     * TRX's library returns none (its `buttonConfig` whitelist is
     * appearance-only and `onPaymentResult` carries just the encrypted token),
     * so it is absent on the TRX path; a sheet we drive ourselves fills it.
     */
    readonly contact?: WalletContact;
}

/** Imperative methods the parent calls via ref. */
export interface TrxJsLibraryInputHandle {
    /**
     * Extract the encrypted payload from the JS library and return it.
     * Throws on any of: not initialized, library-side validation
     * failure, or empty Encrypted block. Parent decides whether to
     * surface to UI or retry — call `enableFields()` to re-activate
     * fields after a failed extract.
     */
    extractEncryptedData(): Promise<TrxEncryptedPayload>;
    /**
     * Re-activate fields after a failed `extractEncryptedData()` call.
     * Per §11.6 the extract call deactivates fields on every
     * invocation regardless of outcome.
     */
    enableFields(): void;
}

export interface TrxJsLibraryInputProps {
    /** Required to mint a session against the right TRX restaurant. */
    readonly tenantId: number;
    readonly restaurantId: number;
    /** Card vs. ACH. Drives which fields are mounted + which extract API is used. */
    readonly paymentType?: 'card' | 'ach';
    /**
     * Required for innerAction = Sale | Auth (server enforces). Omit
     * for Avs / Verify (innerAction can also be omitted to default to Sale).
     */
    readonly amount?: {
        readonly minorUnits: number;
        readonly currency: 'USD' | 'CAD';
    };
    /** Optional — echoes onto Reference.PurchaseId for orderId tracking. */
    readonly purchaseId?: string;
    /** Optional — echoes onto Reference.Guid for inbound-webhook dedup. */
    readonly idempotencyKey?: string;
    /**
     * Where the PayPage session comes from. Omitted, the form mints it through
     * the API as the signed-in customer (checkout, pay at table). The POS
     * keyed-entry page injects one that asks the NATIVE till to mint over the
     * WebView bridge, because a page inside a WebView can never hold the
     * credentials that call needs (App Check refuses a WebView). The form still
     * composes the request — innerAction, rails, amount — so those rules live
     * once, here. Must be referentially stable: a new function re-mints.
     */
    readonly mintSession?: TrxPayPageSessionMinter;
    /**
     * Surface library-init errors and per-extract failures to the
     * parent (e.g. for toast display). Distinct from React errors — a
     * `permissions-error` from §11.9 surfaces here, not via React's
     * error boundary.
     */
    readonly onValidationError?: (message: string) => void;
    /**
     * Fires whenever the library's readiness flips: `true` once the
     * script, session, and field iframes are all initialized (extract
     * will not throw the not-ready error), `false` while loading or
     * after a re-init. Parents use this to gate their submit button —
     * the ref mounting alone does NOT mean the fields are usable.
     */
    readonly onReady?: (ready: boolean) => void;
    /**
     * Render Apple Pay / Google Pay buttons (card sessions only). The TRX JS
     * library mounts the native wallet buttons into our containers and clears
     * them on init. Requires the TRX merchant account to support wallets +
     * (Apple Pay) a verified domain.
     */
    readonly enableWallets?: boolean;
    /**
     * Fires when the customer completes an Apple/Google Pay sheet. The parent
     * submits the checkout immediately with this payload (1-min cryptogram TTL).
     */
    readonly onWalletResult?: (result: TrxWalletPaymentResult) => void;
}

// ─────────────────────────── iframe interior styling ───────────────────
//
// The ONLY surface we can style inside the cross-origin field iframe. Both
// stylesheets and the host-DOM brand mark now come from `@order-up/common`
// (imported above), which owns the single copy — see `trxFieldStyles.ts`.
// They lived here as local constants, and the card-number artwork
// suppression that this file pioneered never reached the shared components,
// so the Books invoice charge dialog shipped with the brand logo tiled over
// the digits. Fix it there, and checkout inherits it.

// ─────────────────────────── host-DOM styling (injected once) ───────────
//
// Keyframes + the slab structural CSS that references the brand var and
// `:focus-within` (not cleanly expressible as Tailwind utilities).
// Injected a single time per document, deduped by id.

const PAY_STYLE_ID = 'op-trx-paypage-styles';
const PAY_STYLES = `
@keyframes op-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes op-pop{0%{opacity:0;transform:scale(.6) translateY(-50%)}60%{opacity:1;transform:scale(1.08) translateY(-50%)}100%{transform:scale(1) translateY(-50%)}}
@keyframes op-shimmer{0%{background-position:180% 0}100%{background-position:-180% 0}}
@keyframes op-shake{10%,90%{transform:translateX(-1px)}20%,80%{transform:translateX(2px)}30%,50%,70%{transform:translateX(-4px)}40%,60%{transform:translateX(4px)}}

.op-pay{--op-accent:var(--brand-primary,#18181B)}
.op-pay-slab{
  position:relative;border:1px solid #E7E8EC;border-radius:18px;
  background:
    radial-gradient(130% 120% at 0% -10%,rgba(255,255,255,.9),transparent 45%),
    linear-gradient(180deg,#FFFFFF 0%,#FBFBFD 100%);
  box-shadow:0 1px 2px rgba(15,18,34,.04),0 10px 30px -16px rgba(15,18,34,.12);
  transition:box-shadow .28s cubic-bezier(.2,.7,.2,1),border-color .2s ease,transform .25s cubic-bezier(.2,.7,.2,1);
}
.op-pay-slab:focus-within{
  border-color:color-mix(in srgb,var(--op-accent) 75%,#E7E8EC);
  box-shadow:
    0 1px 2px rgba(15,18,34,.04),
    0 18px 40px -20px color-mix(in srgb,var(--op-accent) 55%,transparent),
    0 0 0 4px color-mix(in srgb,var(--op-accent) 14%,transparent);
  transform:translateY(-1px);
}
.op-pay-slab[data-error="true"]{
  border-color:#E5484D;
  box-shadow:0 1px 2px rgba(15,18,34,.04),0 0 0 4px rgba(229,72,77,.13);
  animation:op-shake .42s cubic-bezier(.36,.07,.19,.97);
}
.op-pay-row{position:relative;padding:13px 18px}
.op-pay-row+.op-pay-row{border-top:1px solid #EFF0F3}
.op-pay-split{display:grid;grid-template-columns:1fr 1px 1fr}
.op-pay-split .op-pay-vdiv{background:#EFF0F3}
.op-pay-split .op-pay-col{padding:13px 18px}
.op-pay-label{display:block;font-size:10.5px;letter-spacing:.07em;text-transform:uppercase;font-weight:700;color:#8B909B;margin-bottom:5px}
.op-pay-cell{height:26px}
.op-pay-cellwrap{position:relative;height:26px}
.op-pay-cellwrap--number{margin-right:48px}
.op-pay-brand{position:absolute;right:18px;top:calc(50% + 7px);transform:translateY(-50%);height:26px;display:flex;align-items:center;pointer-events:none}
.op-pay-chip{animation:op-pop .3s cubic-bezier(.2,.8,.2,1) both;display:block}
.op-pay-rowin{animation:op-rise .55s cubic-bezier(.2,.7,.2,1) both}

/* Per-cell shimmer — only the input areas wait on the TRX library, so
   only they pulse. Labels/header/footer render immediately. */
.op-pay-shim{border-radius:7px;background:linear-gradient(90deg,#EEF0F3 8%,#F7F8FA 28%,#EEF0F3 48%);background-size:240% 100%;animation:op-shimmer 1.35s linear infinite}
.op-pay-cellskel{position:absolute;inset:0;border-radius:7px;pointer-events:none}
@media (prefers-reduced-motion:reduce){
  .op-pay-rowin,.op-pay-chip,.op-pay-slab,.op-pay-shim,.op-pay-slab[data-error="true"]{animation:none!important;transition:none!important}
}
`.trim();

function ensurePayStyles() {
    if (typeof document === 'undefined') return;
    if (document.getElementById(PAY_STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = PAY_STYLE_ID;
    el.textContent = PAY_STYLES;
    document.head.appendChild(el);
}

// ─────────────────────────── component ─────────────────────────────────

/** Shape of TRX errors when surfaced as Error.cause for parent consumption. */
interface ExtractError extends Error {
    readonly trxErrors?: readonly TrxPayPageError[];
}

/**
 * Build a customer-facing error message from a TRX library error.
 * The library's error types (§11.9) are technical; we map the most
 * common ones to user-readable messages and fall through to the raw
 * message for the rest.
 */
function describeTrxError(errors: readonly TrxPayPageError[] | undefined): string {
    if (!errors || errors.length === 0) {
        return 'Payment input could not be processed. Please try again.';
    }
    const first = errors[0];
    // Both spellings. The live bundle emits `errorType`, so switching on
    // `first.type` alone hit `default` for EVERY real error and returned the
    // generic card message — which is why a permissions-error surfaced as
    // "Please check your payment information and try again." in prod.
    switch (trxErrorType(first)) {
        case 'wrong-field':
            return 'Please check your payment information and try again.';
        case 'permissions-error':
            return 'This restaurant is not currently configured for online payments. Please contact the restaurant.';
        case 'server-error':
            return 'Payment service temporarily unavailable. Please try again in a moment.';
        case 'iframe-error':
        case 'container-error':
        case 'styles-error':
        case 'wrong-config':
            return 'Payment form failed to load. Please refresh the page and try again.';
        default:
            return (
                trxErrorMessage(first) ||
                'Please check your payment information and try again.'
            );
    }
}

// ─────────────────────────── load supervision ──────────────────────────
//
// Nothing in the load chain below has a deadline of its own, and the part we
// do not own has none either: TRX's `PayPageJS.initialize()` resolves only
// once its hidden orchestrator frame has fetched `pay.trxservices.com/api/
// PayPage/Init` and posted `paymentMethod` back — an un-timed fetch inside a
// cross-origin frame, behind a frame handshake the library itself gives 30s.
// When any link stalls the form shimmers forever, the Place-order button
// reads "Loading payment form…", and the only way out was a reload. House of
// Pho's production PayPage sessions (2026-09-16..25) show it: 16 of ~75
// checkout loads re-minted the SAME cart total 2–5 times within seconds to
// minutes — customers reloading a stuck form.
//
// So every load stage now has a deadline. A stage that overruns (or fails
// outright) is reported, and the whole form is REMOUNTED under a new key —
// a fresh script load, session and library instance, and the stuck
// instance's iframes leave the DOM with the old subtree. Retrying inside one
// mount is not an option: the library is non-idempotent and appends frames
// into our containers. After MAX_AUTO_RETRIES the customer gets a "Try
// again" button that does the same remount — never "please refresh", which
// discards the page state they already have.

/** Which link of the load chain the form is waiting on. */
type TrxFormStage = 'script' | 'session' | 'initialize' | 'ready';

/**
 * Per-stage deadline. Measured on production House of Pho (desktop Chrome,
 * 2026-09-25): script ~0.3s, session mint ~0.8s, library init ~1.3s. A slow
 * phone can take several times that, so the deadline is per STAGE — a load
 * that is progressing, however slowly, is never torn down; only one that has
 * stopped moving is.
 */
export const TRX_FORM_STAGE_TIMEOUT_MS = 10_000;

/** Silent remounts before the customer is shown a "Try again" button. */
export const TRX_FORM_MAX_AUTO_RETRIES = 2;

interface TrxPayPageFormSupervision {
    /** Which mount this is — part of the container ids, so no two share one. */
    readonly attempt: number;
    /** Auto-retries are spent: show the manual retry instead of a shimmer. */
    readonly exhausted: boolean;
    /** This mount stopped moving (or failed) at `stage`. Called at most once. */
    readonly onStall: (stage: TrxFormStage, cause: 'timeout' | 'error') => void;
    readonly onManualRetry: () => void;
}

/** Supplies a PayPage session for the request the form composed. */
export type TrxPayPageSessionMinter = (
    request: TrxPayPageSessionRequest,
) => Promise<TrxPayPageSessionResponse>;

/**
 * The customer default: the API mint, as whoever this browser is signed in as.
 * Loaded on first use, not imported: the API client pulls in the Firebase SDK,
 * and the POS keyed-entry bundle — which never mints this way — must not ship
 * it to the till's WebView.
 */
const mintViaApi: TrxPayPageSessionMinter = async (request) => {
    const { initTrxPayPageSession } = await import('@order-up/client/vite');
    return initTrxPayPageSession(request);
};

const TrxJsLibraryInput = forwardRef<
    TrxJsLibraryInputHandle,
    TrxJsLibraryInputProps
>(function TrxJsLibraryInput(props, handleRef) {
    const [attempt, setAttempt] = useState(0);
    const [exhausted, setExhausted] = useState(false);
    const autoRetriesRef = useRef(0);

    const onStall = useCallback(
        (stage: TrxFormStage, cause: 'timeout' | 'error') => {
            const willRetry = autoRetriesRef.current < TRX_FORM_MAX_AUTO_RETRIES;
            // A stall is invisible otherwise: nothing throws, so before this
            // the only record of one was a customer phoning the restaurant.
            Sentry.captureMessage('TRX card form stalled', {
                level: willRetry ? 'warning' : 'error',
                tags: {
                    trx_form_stage: stage,
                    trx_form_cause: cause,
                    trx_form_attempt: String(attempt),
                    trx_form_will_retry: String(willRetry),
                },
            });
            logger.warn('TRX card form stalled', { stage, cause, attempt, willRetry });
            if (willRetry) {
                autoRetriesRef.current += 1;
                setAttempt((a) => a + 1);
            } else {
                setExhausted(true);
            }
        },
        [attempt],
    );

    const onManualRetry = useCallback(() => {
        autoRetriesRef.current = 0;
        setExhausted(false);
        setAttempt((a) => a + 1);
    }, []);

    return (
        <TrxPayPageForm
            key={attempt}
            ref={handleRef}
            {...props}
            attempt={attempt}
            exhausted={exhausted}
            onStall={onStall}
            onManualRetry={onManualRetry}
        />
    );
});

const TrxPayPageForm = forwardRef<
    TrxJsLibraryInputHandle,
    TrxJsLibraryInputProps & TrxPayPageFormSupervision
>(function TrxPayPageForm(
    {
        tenantId,
        restaurantId: locationNumericId,
        paymentType = 'card',
        amount,
        purchaseId,
        idempotencyKey,
        mintSession = mintViaApi,
        onValidationError,
        onReady,
        enableWallets = false,
        onWalletResult,
        attempt,
        exhausted,
        onStall,
        onManualRetry,
    },
    handleRef,
) {
    const { ready: scriptReady, error: scriptError, PayPageJS } =
        useTrxPayPageScript();

    // Inject the host stylesheet once.
    useEffect(() => {
        ensurePayStyles();
    }, []);

    // Unique container ID prefix per instance — multiple
    // TrxJsLibraryInput components can co-exist on a page (e.g. a
    // checkout flow with a saved-card option that re-renders) without
    // clashing on the global container IDs. The attempt is part of it: a
    // remount sits at the same tree position, so useId alone would hand the
    // new mount the stalled instance's ids — and that instance, if its
    // initialize() resolves late, destroys itself into whatever holds them.
    const idPrefix = `${useId().replace(/[:]/g, '')}a${attempt}`;
    const containerIds = {
        cardNumber: `${idPrefix}-card-number`,
        expirationDate: `${idPrefix}-expiration-date`,
        cvv: `${idPrefix}-cvv`,
        routing: `${idPrefix}-routing`,
        accountNumber: `${idPrefix}-account-number`,
        applePay: `${idPrefix}-apple-pay`,
        googlePay: `${idPrefix}-google-pay`,
    } as const;

    const [session, setSession] = useState<TrxPayPageSessionResponse | null>(null);
    const [sessionError, setSessionError] = useState<Error | null>(null);
    const [initError, setInitError] = useState<Error | null>(null);
    const instanceRef = useRef<TrxPayPageInstance | null>(null);
    const [instanceReady, setInstanceReady] = useState(false);
    const [brand, setBrand] = useState<CardBrand>('generic');
    const [cardError, setCardError] = useState(false);

    // Stable ref for the parent's onValidationError callback. The parent
    // (CheckoutPage) passes a NEW inline arrow on every render; if we depend on
    // it directly, the session-mint and init effects below re-fire on every
    // parent re-render — re-minting the PayPage session and re-initializing the
    // (non-idempotent) TRX library into the same containers, which races and
    // makes initialize() report errors. Routing through a ref keeps the latest
    // callback without making it an effect dependency.
    const onValidationErrorRef = useRef(onValidationError);
    useEffect(() => {
        onValidationErrorRef.current = onValidationError;
    }, [onValidationError]);

    // Same ref pattern for the wallet-result callback — keeps the init effect
    // from re-firing (and re-minting/re-initializing the library) on every
    // parent re-render that passes a fresh inline callback.
    const onWalletResultRef = useRef(onWalletResult);
    useEffect(() => {
        onWalletResultRef.current = onWalletResult;
    }, [onWalletResult]);

    // Apple Pay on the web is Safari-ONLY (`window.ApplePaySession`). Mounting
    // its button in Chrome/Firefox makes the TRX library report "Apple Pay is
    // not supported on this browser" into instance.errors, which we must NOT let
    // block the card form. Probe once at mount — capability can't change within
    // a session — and only offer the Apple Pay button where it can actually be
    // presented. Google Pay works cross-browser, so it stays on `enableWallets`.
    const applePayAvailable = enableWallets && isApplePayAvailable();

    // Only WHETHER an amount exists matters to the session (it selects the
    // innerAction) — not its value. Hoisted so it can be a clean,
    // statically-checkable dependency of the mint effect below.
    const hasAmount = amount !== undefined;
    // A Sale for $0.00 is refused by TRX (`X1 Validation Error: Amount must be
    // greater than 0.00 for Sale`), and a caller passes one while its total is
    // still loading. Minting then burnt every supervisor retry inside the same
    // second, and the card fields never mounted (measured on the dev money
    // journey, 2026-09-29). Wait for a real amount instead of minting a session
    // that cannot succeed.
    const amountReady = !hasAmount || amount.minorUnits > 0;

    // ── Mint the session ────────────────────────────────────────────
    //
    // No synchronous reset of state inside the effect — that violates
    // the React-lint cascading-renders rule. The brief window where an
    // in-flight dep change would show the previous session is harmless:
    // the prior session is still server-valid for whatever values
    // minted it, and the backend rejects mismatches at submit time.
    // When the new fetch resolves, state transitions atomically.
    // Never mint against a placeholder restaurant — the one place every caller
    // (customer checkout and the POS keyed-entry page) reaches the mint.
    const hasLocationScope = isPositiveIntegerId(tenantId) && isPositiveIntegerId(locationNumericId);

    useEffect(() => {
        if (!hasLocationScope) {
            onValidationErrorRef.current?.(MISSING_LOCATION_CONTEXT);
            return;
        }
        if (!amountReady) return;
        let cancelled = false;
        mintSession({
            tenantId,
            restaurantId: locationNumericId,
            // A zero-amount session is rail-specific, and getting it wrong is
            // not a soft failure: `Verify` is ACH-only (§4.2) and TRX answers a
            // Card session asking for it with `X0 / Validation Error` and no
            // payPageId, so the hosted fields never mount. `Avs` is the §4.1
            // Credit equivalent — "standalone address verification (no
            // charge)". The adapter now refuses the wrong pairing outright.
            innerAction: hasAmount
                ? 'Sale'
                : paymentType === 'ach'
                  ? 'Verify'
                  : 'Avs',
            ...(hasAmount ? { amount } : {}),
            ...(purchaseId !== undefined ? { purchaseId } : {}),
            ...(idempotencyKey !== undefined ? { idempotencyKey } : {}),
            // The block MUST be present: a session with no PaymentTypes
            // 500s TRX's own /api/PayPage/Init with a null reference and the
            // card form never mounts. The rule is SHARED with the server's own
            // mint (pay at table) — see
            // packages/common/src/payments/payPageRails.ts.
            paymentTypes: resolvePayPageRails({
                paymentType,
                enableWallets,
                hasAmount,
            }),
        })
            .then((res) => {
                if (!cancelled) {
                    setSession(res);
                    setSessionError(null);
                }
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err instanceof Error ? err : new Error(String(err));
                logger.error('Failed to mint TRX PayPage session', e);
                // No "please refresh" here: the supervisor remounts the form
                // (a fresh mint) and only asks the customer to act once its
                // retries are spent.
                setSessionError(e);
            });
        return () => {
            cancelled = true;
        };
        // Depend on WHETHER an amount exists (which selects innerAction
        // Sale vs Verify) — NOT on its value. The PayPage session only
        // encrypts the card-field iframes; the real charge runs through our
        // own Sale adapter with the server-computed `chargeAmount`, and
        // NOTHING validates the session's minted amount against the final
        // charge. So a tip change — which moves
        // `amount.minorUnits` — must NOT re-mint a fresh session: that was a
        // wasted gateway round-trip on every tip adjustment (and a race if the
        // customer changed tips faster than the mints resolved). `extract()`
        // at submit reads the card fields, which are amount-independent, so a
        // session minted at the initial amount stays valid through tip edits.
        // `idempotencyKey` is dropped too — it's never threaded to this
        // component (set only at submit time), so it added no value here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tenantId,
        locationNumericId,
        hasLocationScope,
        paymentType,
        hasAmount,
        amountReady,
        purchaseId,
        // Re-mint when the wallet ask changes: it now selects the session's
        // declared payment types, so a session minted while it was false can
        // never mount a wallet button no matter what the init config says.
        enableWallets,
        // A different source is a different principal minting — stable for
        // the life of a POS launch, the module default on customer checkout.
        mintSession,
    ]);

    // ── Initialize the JS library once script + session are ready ───
    useEffect(() => {
        if (!scriptReady || !PayPageJS || !session) return;

        let cancelled = false;
        let local: TrxPayPageInstance | null = null;

        // Build the fields map per paymentType. Card vs. ACH require
        // different containers (§11.4) — mounting a stale Card map for
        // an ACH session causes the library to throw `wrong-config`.
        // Map a TRX wallet `onPaymentResult` to the parent callback. SUCCESS →
        // surface the encrypted payload + cardSecurity; FAILED → error toast;
        // CANCELLED → no-op (the customer dismissed the sheet).
        const makeWalletHandler =
            (walletName: 'ApplePay' | 'GooglePay') =>
            (result: TrxPayPageWalletResult) => {
                if (result.status === 'SUCCESS' && result.data) {
                    const cs = result.data.cardSecurity;
                    onWalletResultRef.current?.({
                        walletName,
                        data: result.data.data,
                        keyId: result.data.keyId,
                        ...(result.data.bin ? { bin: result.data.bin } : {}),
                        ...(cs
                            ? {
                                  cardSecurity: {
                                      ...(cs.cavv ? { cavv: cs.cavv } : {}),
                                      ...(cs.dsrpCryptogram
                                          ? { dsrpCryptogram: cs.dsrpCryptogram }
                                          : {}),
                                  },
                              }
                            : {}),
                    });
                } else if (result.status === 'FAILED') {
                    onValidationErrorRef.current?.(
                        result.message ??
                            'Wallet payment could not be completed. Please try another method.',
                    );
                }
            };

        const fieldsMap: TrxPayPageFieldsMap =
            paymentType === 'ach'
                ? {
                      routing: {
                          containerId: containerIds.routing,
                          stylesCSS: FIELD_CSS_BASE,
                      },
                      'account-number': {
                          containerId: containerIds.accountNumber,
                          stylesCSS: FIELD_CSS_BASE,
                      },
                  }
                : {
                      'card-number': {
                          containerId: containerIds.cardNumber,
                          stylesCSS: FIELD_CSS_NUMBER,
                      },
                      'expiration-date': {
                          containerId: containerIds.expirationDate,
                          stylesCSS: FIELD_CSS_BASE,
                      },
                      cvv: {
                          containerId: containerIds.cvv,
                          stylesCSS: FIELD_CSS_BASE,
                      },
                      // Apple/Google Pay buttons — card sessions only. The
                      // library mounts native wallet buttons into our containers.
                      // Apple Pay is offered ONLY where the browser supports it
                      // (Safari); mounting it in Chrome/Firefox makes the library
                      // emit an "unsupported" init error that must never reach the
                      // card form. Google Pay is cross-browser.
                      ...(applePayAvailable
                          ? {
                                'apple-pay': {
                                    containerId: containerIds.applePay,
                                    onPaymentResult: makeWalletHandler('ApplePay'),
                                    buttonConfig: { buttonType: 'check-out' },
                                },
                            }
                          : {}),
                      ...(enableWallets
                          ? {
                                'google-pay': {
                                    containerId: containerIds.googlePay,
                                    onPaymentResult: makeWalletHandler('GooglePay'),
                                    buttonConfig: { buttonType: 'checkout' },
                                },
                            }
                          : {}),
                  };

        PayPageJS.initialize({
            sessionData: {
                clientId: session.clientId,
                payPageId: session.payPageId,
                sessionId: session.sessionId,
            },
            includeBIN: paymentType === 'card',
            includeACHInfo: paymentType === 'ach',
            // Live card-network detection — surfaced to our DOM so we can
            // morph the brand mark as the cardholder types. The only
            // cross-iframe signal the library exposes; we lean on it.
            // TRX requires a function on EVERY initialize, bank-only fields
            // included ("Set a valid 'onCardTypeChange' callback function"
            // otherwise), so a bank session passes one that ignores the brand.
            onCardTypeChange:
                paymentType === 'card'
                    ? (cardType: string) => {
                          if (!cancelled) setBrand(normalizeCardBrand(cardType));
                      }
                    : () => {
                          // No card-number field is mounted on a bank session.
                      },
            fields: fieldsMap,
        })
            .then((instance) => {
                if (cancelled) {
                    // Race: if the parent unmounted before init resolved,
                    // tear down the instance immediately so we don't
                    // leak iframes or listeners.
                    instance.destroy();
                    return;
                }
                instanceRef.current = instance;
                local = instance;
                setInstanceReady(true);

                if (instance.errors && instance.errors.length > 0) {
                    // An OPTIONAL wallet button failing to mount (e.g. Apple Pay
                    // in a non-Safari browser) must NEVER block the required card
                    // form. Split wallet-only errors off and surface only the
                    // ones that actually affect card entry.
                    const { blocking, walletOnly } = partitionTrxInitErrors(
                        instance.errors,
                    );
                    if (walletOnly.length > 0) {
                        logger.warn('TRX library reported wallet init errors (non-blocking)', {
                            errors: walletOnly,
                        });
                    }
                    if (blocking.length > 0) {
                        logger.warn('TRX library reported init errors', {
                            errors: blocking,
                        });
                        onValidationErrorRef.current?.(describeTrxError(blocking));
                    }
                }
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err instanceof Error ? err : new Error(String(err));
                logger.error('TRX PayPage initialize failed', e);
                // Reported to the supervisor, which remounts — see the stall
                // effect below.
                setInitError(e);
            });

        return () => {
            cancelled = true;
            if (local) {
                try {
                    local.destroy();
                } catch (err) {
                    logger.warn('Error destroying TRX PayPage instance', {
                        error: err instanceof Error ? err.message : String(err),
                    });
                }
                instanceRef.current = null;
                setInstanceReady(false);
            }
        };
    }, [scriptReady, PayPageJS, session, paymentType, enableWallets, applePayAvailable, containerIds.cardNumber, containerIds.expirationDate, containerIds.cvv, containerIds.routing, containerIds.accountNumber, containerIds.applePay, containerIds.googlePay]);

    // ── Imperative handle for the parent ────────────────────────────
    const extractEncryptedData = useCallback(async (): Promise<TrxEncryptedPayload> => {
        const instance = instanceRef.current;
        if (!instance) {
            throw new Error('TRX PayPage instance is not ready');
        }

        // Local error visual: flag before throwing so the slab rings red
        // and shakes for the cardholder even though the parent owns the
        // human-readable message. Cleared on next focus (focusin below).
        const fail = (errors: readonly TrxPayPageError[] | undefined): never => {
            setCardError(true);
            const err: ExtractError = Object.assign(
                new Error(describeTrxError(errors)),
                {
                    trxErrors: errors,
                    // LOAD-BEARING. `CheckoutPage` renders this through
                    // `translateError`, whose generic fallback DISCARDS
                    // `error.message`. Without a code the shopper is told
                    // "Something went wrong" for a mistyped card number — the
                    // same defect that hid True Wealth invoice 1042 from the
                    // operator on 2026-09-03.
                    code:
                        paymentType === 'ach'
                            ? BANK_FIELDS_REJECTED
                            : CARD_FIELDS_REJECTED,
                },
            );
            throw err;
        };

        if (paymentType === 'ach') {
            const result: TrxPayPageAchEncryptedResult =
                await instance.getEncryptedAchData();
            if (!result.isSuccess || !result.Encrypted) {
                fail(result.errors);
            }
            return {
                paymentType: 'ach',
                data: result.Encrypted!.Data,
                keyId: result.Encrypted!.KeyId,
                ...(result.Encrypted!.RoutingNumber !== undefined
                    ? { routingNumber: result.Encrypted!.RoutingNumber }
                    : {}),
                ...(result.Encrypted!.AccountNumberLastFour !== undefined
                    ? { accountNumberLastFour: result.Encrypted!.AccountNumberLastFour }
                    : {}),
            };
        }

        const result: TrxPayPageCardEncryptedResult =
            await instance.getEncryptedCardData();
        if (!result.isSuccess || !result.Encrypted) {
            fail(result.errors);
        }
        return {
            paymentType: 'card',
            data: result.Encrypted!.Data,
            keyId: result.Encrypted!.KeyId,
            ...(result.Encrypted!.BIN !== undefined ? { bin: result.Encrypted!.BIN } : {}),
        };
    }, [paymentType]);

    const enableFields = useCallback(() => {
        const instance = instanceRef.current;
        if (!instance) return;
        if (paymentType === 'ach') {
            instance.enableAchDataFields();
        } else {
            instance.enableCardDataFields();
        }
    }, [paymentType]);

    useImperativeHandle(
        handleRef,
        () => ({ extractEncryptedData, enableFields }),
        [extractEncryptedData, enableFields],
    );

    // ── Render ──────────────────────────────────────────────────────

    const isLoading = !scriptReady || !session || !instanceReady;
    const scopeError = hasLocationScope ? null : new Error(MISSING_LOCATION_CONTEXT);
    // A load failure a remount can cure. `scopeError` is not one: no retry
    // gives a placeholder restaurant a real id, so it is shown, never retried.
    const loadError = scriptError ?? sessionError ?? initError;

    const stage: TrxFormStage = !scriptReady
        ? 'script'
        : !session
          ? 'session'
          : !instanceReady
            ? 'initialize'
            : 'ready';

    // ── Stall supervision ───────────────────────────────────────────
    // One report per mount: the watchdog and an error can both fire for the
    // same dead load, and the supervisor must remount once, not twice.
    const stallReportedRef = useRef(false);
    const onStallRef = useRef(onStall);
    useEffect(() => {
        onStallRef.current = onStall;
    }, [onStall]);
    const reportStall = useCallback((s: TrxFormStage, cause: 'timeout' | 'error') => {
        if (stallReportedRef.current) return;
        stallReportedRef.current = true;
        onStallRef.current(s, cause);
    }, []);

    // The watchdog is re-armed on every stage change, so each stage gets the
    // full deadline and a slow-but-moving load is left alone.
    // Every dep here must be stable across renders: a dep that changes each
    // render (a fresh Error object, say) re-arms the timer each render, and a
    // parent that re-renders often would then keep the watchdog from EVER
    // firing.
    const hasLoadError = loadError !== null;
    useEffect(() => {
        if (stage === 'ready' || !hasLocationScope || hasLoadError || exhausted) return;
        const timer = setTimeout(() => reportStall(stage, 'timeout'), TRX_FORM_STAGE_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [stage, hasLocationScope, hasLoadError, exhausted, reportStall]);

    useEffect(() => {
        if (loadError && !exhausted) reportStall(stage, 'error');
    }, [loadError, exhausted, stage, reportStall]);

    // Time-to-ready, so a slow form is a number rather than an anecdote.
    const mountedAtRef = useRef(Date.now());
    useEffect(() => {
        if (stage !== 'ready') return;
        const ms = Date.now() - mountedAtRef.current;
        logger.info('TRX card form ready', { attempt, ms });
        if (attempt > 0) {
            Sentry.captureMessage('TRX card form recovered after remount', {
                level: 'info',
                tags: { trx_form_attempt: String(attempt) },
            });
        }
    }, [stage, attempt]);

    // Notify the parent on every readiness flip (init complete AND
    // teardown/re-init) so its submit gate tracks the real field state.
    useEffect(() => {
        onReady?.(!isLoading);
    }, [isLoading, onReady]);

    const isCard = paymentType === 'card';
    // Retries in flight keep the shimmer; only a spent budget (or a scope
    // error, which no retry fixes) replaces it with a message.
    const showFailure = isLoading && (!!scopeError || exhausted);
    const showSkeleton = isLoading && !showFailure;

    return (
        <div className="op-pay mb-7">
            {/* Header — quiet, premium. A label + a subtle live lock. */}
            <div className="mb-3 flex items-end justify-between">
                <div>
                    <h2 className="text-[15px] font-semibold tracking-tight text-zinc-900">
                        {isCard ? 'Card details' : 'Bank account'}
                    </h2>
                    <p className="mt-0.5 text-[12px] text-zinc-400">
                        {isCard
                            ? 'Encrypted end-to-end — your card never touches our servers.'
                            : 'Encrypted end-to-end — your account never touches our servers.'}
                    </p>
                </div>
                <div className="flex items-center gap-1.5 text-zinc-400">
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">Secure</span>
                </div>
            </div>

            {/* Apple Pay / Google Pay buttons (card sessions only). The TRX
                library mounts native wallet buttons into these containers and
                clears them on init — render empty divs and let the library own
                their contents. Wallet-first per the Google Pay best-practice
                guidance; the card form follows under an "or" divider. */}
            {isCard && enableWallets && (
                <div className="mb-4">
                    <div className="flex flex-col gap-2">
                        {/* Apple Pay container renders ONLY where the library
                            will mount it (Safari) — otherwise an empty slot and
                            a spurious "unsupported" init error. Google Pay is
                            cross-browser. */}
                        {applePayAvailable && <div id={containerIds.applePay} />}
                        <div id={containerIds.googlePay} />
                    </div>
                    <div className="my-4 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">
                        <span className="h-px flex-1 bg-zinc-200" />
                        or pay with card
                        <span className="h-px flex-1 bg-zinc-200" />
                    </div>
                </div>
            )}

            {/* The slab. Per-field iframes mount into the cells below. The
                TRX library auto-injects a hidden orchestrator iframe into
                the FIRST container (§11.5) — keep the card-number / routing
                cell first; do not reorder. */}
            <div
                className="op-pay-slab"
                data-error={cardError ? 'true' : 'false'}
                onFocusCapture={() => cardError && setCardError(false)}
            >
                {/* Labels + structure render immediately; only the input
                    cells (which actually wait on the TRX library) shimmer.
                    The cell DIVs MUST stay mounted so the library can mount
                    its iframes into them. */}
                {isCard ? (
                    <>
                        <div className="op-pay-row op-pay-rowin" style={{ animationDelay: '40ms' }}>
                            <label className="op-pay-label" htmlFor={containerIds.cardNumber}>
                                Card number
                            </label>
                            <div className="op-pay-cellwrap op-pay-cellwrap--number">
                                <div id={containerIds.cardNumber} className="op-pay-cell" />
                                {showSkeleton && <span className="op-pay-cellskel op-pay-shim" />}
                            </div>
                            {!showSkeleton && (
                                <div className="op-pay-brand">
                                    {/* key forces the pop-in to replay on every brand change */}
                                    <TrxCardBrandMark key={brand} brand={brand} />
                                </div>
                            )}
                        </div>
                        <div className="op-pay-row op-pay-split op-pay-rowin" style={{ padding: 0, animationDelay: '110ms' }}>
                            <div className="op-pay-col">
                                <label className="op-pay-label" htmlFor={containerIds.expirationDate}>
                                    Expiry
                                </label>
                                <div className="op-pay-cellwrap">
                                    <div id={containerIds.expirationDate} className="op-pay-cell" />
                                    {showSkeleton && <span className="op-pay-cellskel op-pay-shim" />}
                                </div>
                            </div>
                            <div className="op-pay-vdiv" />
                            <div className="op-pay-col">
                                <label className="op-pay-label" htmlFor={containerIds.cvv}>
                                    CVC
                                </label>
                                <div className="op-pay-cellwrap">
                                    <div id={containerIds.cvv} className="op-pay-cell" />
                                    {showSkeleton && <span className="op-pay-cellskel op-pay-shim" />}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="op-pay-row op-pay-rowin" style={{ animationDelay: '40ms' }}>
                            <label className="op-pay-label" htmlFor={containerIds.routing}>
                                Routing number
                            </label>
                            <div className="op-pay-cellwrap">
                                <div id={containerIds.routing} className="op-pay-cell" />
                                {showSkeleton && <span className="op-pay-cellskel op-pay-shim" />}
                            </div>
                        </div>
                        <div className="op-pay-row op-pay-rowin" style={{ animationDelay: '110ms' }}>
                            <label className="op-pay-label" htmlFor={containerIds.accountNumber}>
                                Account number
                            </label>
                            <div className="op-pay-cellwrap">
                                <div id={containerIds.accountNumber} className="op-pay-cell" />
                                {showSkeleton && <span className="op-pay-cellskel op-pay-shim" />}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Failure display — distinct from per-field validation surfaced
                via onValidationError. Shown only once the supervisor's silent
                retries are spent (or for a scope error, which no retry
                fixes). "Try again" remounts just this form; the rest of the
                checkout — tip, contact details, cart — is left as it is. */}
            {showFailure && (
                <div
                    className="mt-3 flex items-start gap-2.5 rounded-2xl border border-red-100 bg-red-50/70 px-4 py-3"
                    role="alert"
                >
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-red-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                        <p className="text-[13px] font-medium text-red-700">
                            {scopeError
                                ? MISSING_LOCATION_CONTEXT
                                : "The secure card form didn't load."}
                        </p>
                        {!scopeError && (
                            <button
                                type="button"
                                onClick={onManualRetry}
                                className="mt-2 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-50"
                                data-testid="trx-form-retry"
                            >
                                Try again
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Trust footer — one restrained line. */}
            <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-zinc-400">
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 1.5l6 2.25v4.5c0 3.9-2.55 7.35-6 8.25-3.45-.9-6-4.35-6-8.25v-4.5L10 1.5zm2.78 6.22a.75.75 0 00-1.06-1.06L9 9.38 7.78 8.16a.75.75 0 10-1.06 1.06l1.75 1.75a.75.75 0 001.06 0l3.25-3.25z" clipRule="evenodd" />
                </svg>
                <span>
                    {isCard ? 'PCI-DSS encrypted' : 'Bank-grade encryption'} · powered by TRX
                </span>
            </div>
        </div>
    );
});

export default TrxJsLibraryInput;
